// =============================================
// frontend/src/pages/AdminPage.js
// =============================================
// Ye page admin dashboard hai.
// Admin yahan se:
//   1. Password se login kar sakta hai
//   2. Invitation links generate kar sakta hai
//   3. Existing links dekh, copy, aur revoke kar sakta hai
//
// Ye independent page hai — App.js se bilkul alag hai.
// State sirf is component mein hai.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    LockKeyhole, Zap, CheckCircle2, Check, Copy,
    RefreshCw, Ban, Trash2, LogOut
} from 'lucide-react';
import { buildApiUrl } from '../utils/api';

// Axios instance with credentials (cookies ke liye), pointed at the backend API origin
const api = axios.create({ baseURL: buildApiUrl(''), withCredentials: true });

// -----------------------------------------------
// Helper: Unix timestamp ko readable date banana
// -----------------------------------------------
const formatDate = (unixSec) => {
    if (!unixSec) return '—';
    return new Date(unixSec * 1000).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// -----------------------------------------------
// Helper: Link status string banana
// -----------------------------------------------
const getLinkStatus = (link) => {
    if (link.revoked) return { label: 'Revoked', color: '#ef4444' };
    if (link.expiresAt && Math.floor(Date.now() / 1000) > link.expiresAt) return { label: 'Expired', color: '#f59e0b' };
    if (link.maxUses !== null && link.useCount >= link.maxUses) return { label: 'Used Up', color: '#f59e0b' };
    return { label: 'Active', color: '#22c55e' };
};

const AdminPage = () => {
    // ------- Auth State -------
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);

    // ------- Links State -------
    const [links, setLinks] = useState([]);
    const [linksLoading, setLinksLoading] = useState(false);

    // ------- Generate Form State -------
    const [form, setForm] = useState({
        email: '',
        label: '',
        expiresIn: '6h',
        type: 'one-time',
        maxUses: '',
        customDate: '',
    });
    const [generatedUrl, setGeneratedUrl] = useState('');
    const [generateLoading, setGenerateLoading] = useState(false);
    const [generateError, setGenerateError] = useState('');
    const [copied, setCopied] = useState(false);
    const [copiedLinkId, setCopiedLinkId] = useState(null);

    // ------- Modal State -------
    const [modalConfig, setModalConfig] = useState({ isOpen: false, title: '', message: '', type: 'confirm', onConfirm: null });

    const showConfirm = (title, message, onConfirm) => setModalConfig({ isOpen: true, title, message, type: 'confirm', onConfirm });
    const showAlert = (title, message) => setModalConfig({ isOpen: true, title, message, type: 'alert', onConfirm: null });
    const closeModal = () => setModalConfig({ ...modalConfig, isOpen: false });

    // ------- Fetch Links -------
    const fetchLinks = useCallback(async () => {
        setLinksLoading(true);
        try {
            const res = await api.get('/admin/links');
            setLinks(res.data);
        } catch (err) {
            if (err.response?.status === 401) setIsLoggedIn(false);
        } finally {
            setLinksLoading(false);
        }
    }, []);

    // Login ke baad links load karo
    useEffect(() => {
        if (isLoggedIn) fetchLinks();
    }, [isLoggedIn, fetchLinks]);

    // =============================================
    // Admin Login
    // =============================================
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);
        try {
            await api.post('/admin/login', { password });
            setIsLoggedIn(true);
            setPassword('');
        } catch (err) {
            setLoginError(err.response?.data?.error || 'Login failed. Check your password.');
        } finally {
            setLoginLoading(false);
        }
    };

    // =============================================
    // Admin Logout
    // =============================================
    const handleLogout = async () => {
        await api.post('/admin/logout').catch(() => { });
        setIsLoggedIn(false);
        setLinks([]);
        setGeneratedUrl('');
    };

    // =============================================
    // Generate Invitation Link
    // =============================================
    const handleGenerate = async (e) => {
        e.preventDefault();
        setGenerateError('');
        setGeneratedUrl('');
        setGenerateLoading(true);

        try {
            // expiresIn value determine karo
            let expiresIn = form.expiresIn;
            if (form.expiresIn === 'custom' && form.customDate) {
                // Custom date ko Unix seconds mein convert karo
                expiresIn = String(Math.floor(new Date(form.customDate).getTime() / 1000));
            }

            const payload = {
                email: form.email,
                label: form.label,
                expiresIn,
                type: form.type,
                maxUses: form.maxUses ? Number(form.maxUses) : null,
            };

            const res = await api.post('/admin/links/generate', payload);
            setGeneratedUrl(res.data.url);
            fetchLinks(); // Table refresh karo
        } catch (err) {
            setGenerateError(err.response?.data?.error || 'Failed to generate link.');
        } finally {
            setGenerateLoading(false);
        }
    };

    // =============================================
    // Copy URL to Clipboard
    // =============================================
    const handleCopy = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleCopyTable = (id, url) => {
        if (!url) return;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedLinkId(id);
            setTimeout(() => setCopiedLinkId(null), 2000);
        });
    };

    // =============================================
    // Revoke Single Link
    // =============================================
    const handleRevoke = (id) => {
        showConfirm('Revoke Link', 'Revoke this link? The user will no longer be able to access App Visual Generator.', async () => {
            try {
                await api.delete(`/admin/links/${id}`);
                fetchLinks();
            } catch (err) {
                showAlert('Error', err.response?.data?.error || 'Failed to revoke link.');
            }
        });
    };

    // =============================================
    // Reactivate Single Link
    // =============================================
    const handleReactivate = (id) => {
        showConfirm('Reactivate Link', 'Reactivate this link? The user will regain access to App Visual Generator if the link is not expired or used up.', async () => {
            try {
                await api.put(`/admin/links/${id}/reactivate`);
                fetchLinks();
            } catch (err) {
                showAlert('Error', err.response?.data?.error || 'Failed to reactivate link.');
            }
        });
    };

    // =============================================
    // Revoke All Links
    // =============================================
    const handleRevokeAll = () => {
        showConfirm('Revoke All Links', 'Revoke ALL links? This will block access for all users immediately.', async () => {
            try {
                await api.delete('/admin/links');
                fetchLinks();
            } catch (err) {
                showAlert('Error', err.response?.data?.error || 'Failed to revoke all links.');
            }
        });
    };

    // =============================================
    // Hard Delete Single Revoked Link
    // =============================================
    const handleHardDelete = (id) => {
        showConfirm('Delete Link', 'Permanently delete this revoked link? This action cannot be undone.', async () => {
            try {
                await api.delete(`/admin/links/${id}/hard`);
                fetchLinks();
            } catch (err) {
                showAlert('Error', err.response?.data?.error || 'Failed to delete link.');
            }
        });
    };

    // =============================================
    // Hard Delete All Revoked Links
    // =============================================
    const handleDeleteRevokedAll = () => {
        showConfirm('Delete All Revoked Links', 'Permanently delete ALL revoked links? This action cannot be undone.', async () => {
            try {
                await api.delete('/admin/links/revoked');
                fetchLinks();
            } catch (err) {
                showAlert('Error', err.response?.data?.error || 'Failed to delete revoked links.');
            }
        });
    };

    // =============================================
    // Render: Login Form
    // =============================================
    if (!isLoggedIn) {
        return (
            <div style={styles.page}>
                <div style={styles.loginCard}>
                    <div style={styles.loginHeader}>
                        <div style={styles.lockIcon}><LockKeyhole size={48} color="#60a5fa" strokeWidth={1.5} /></div>
                        <h1 style={styles.loginTitle}>Admin Panel</h1>
                        <p style={styles.loginSub}>App Visual Generator</p>
                    </div>
                    <form onSubmit={handleLogin}>
                        <label style={styles.fieldLabel}>Admin Password</label>
                        <input
                            type="password"
                            style={styles.input}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter admin password"
                            required
                            autoFocus
                        />
                        {loginError && <p style={styles.errorText}>{loginError}</p>}
                        <button type="submit" disabled={loginLoading} style={styles.primaryBtn}>
                            {loginLoading ? 'Logging in…' : 'Login'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // =============================================
    // Render: Admin Dashboard
    // =============================================
    return (
        <div style={styles.page}>
            <div style={styles.dashboard}>

                {/* Header */}
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.dashTitle}>Admin Dashboard</h1>
                        <p style={styles.dashSub}>App Visual Generator — Invitation Management</p>
                    </div>
                    <button onClick={handleLogout} style={styles.logoutBtn}>
                        <LogOut size={16} /> Logout
                    </button>
                </div>

                {/* Generate Link Form */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Generate Invitation Link</h2>
                    <form onSubmit={handleGenerate} style={styles.formGrid}>
                        <div>
                            <label style={styles.fieldLabel}>Microsoft Email *</label>
                            <input
                                type="email"
                                style={styles.input}
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="user@company.com"
                                required
                            />
                        </div>
                        <div>
                            <label style={styles.fieldLabel}>Label (note)</label>
                            <input
                                type="text"
                                style={styles.input}
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="e.g. Marketing Team"
                            />
                        </div>
                        <div>
                            <label style={styles.fieldLabel}>Expiry</label>
                            <select
                                style={styles.input}
                                value={form.expiresIn}
                                onChange={(e) => setForm({ ...form, expiresIn: e.target.value })}
                            >
                                <option value="6h">6 Hours</option>
                                <option value="1d">1 Day</option>
                                <option value="7d">7 Days</option>
                                <option value="30d">30 Days</option>
                                <option value="permanent">Permanent</option>
                                <option value="custom">Custom Date</option>
                            </select>
                        </div>
                        {form.expiresIn === 'custom' && (
                            <div>
                                <label style={styles.fieldLabel}>Custom Expiry Date</label>
                                <input
                                    type="datetime-local"
                                    style={styles.input}
                                    value={form.customDate}
                                    onChange={(e) => setForm({ ...form, customDate: e.target.value })}
                                    required
                                />
                            </div>
                        )}
                        <div>
                            <label style={styles.fieldLabel}>Link Type</label>
                            <select
                                style={styles.input}
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                            >
                                <option value="one-time">One Time</option>
                                <option value="permanent">Permanent (reusable)</option>
                            </select>
                        </div>
                        <div>
                            <label style={styles.fieldLabel}>Max Uses (optional)</label>
                            <input
                                type="number"
                                style={styles.input}
                                value={form.maxUses}
                                onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
                                placeholder="Leave blank for unlimited"
                                min="1"
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            {generateError && <p style={styles.errorText}>{generateError}</p>}
                            <button type="submit" disabled={generateLoading} style={{ ...styles.primaryBtn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                {generateLoading ? 'Generating…' : <><Zap size={18} /> Generate Invitation Link</>}
                            </button>
                        </div>
                    </form>

                    {/* Generated URL */}
                    {generatedUrl && (
                        <div style={styles.generatedBox}>
                            <p style={styles.generatedLabel}>
                                <CheckCircle2 size={16} color="#4ade80" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                                Invitation Link Generated:
                            </p>
                            <div style={styles.urlRow}>
                                <input
                                    readOnly
                                    style={{ ...styles.input, flex: 1, fontSize: '0.8rem', fontFamily: 'monospace' }}
                                    value={generatedUrl}
                                />
                                <button
                                    onClick={() => handleCopy(generatedUrl)}
                                    style={{ ...styles.copyBtn, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                                >
                                    {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
                                </button>
                            </div>
                            <p style={styles.generatedHint}>Share this link via Slack, Teams, WhatsApp, or Email.</p>
                        </div>
                    )}
                </div>

                {/* Links Table */}
                <div style={styles.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h2 style={{ ...styles.cardTitle, margin: 0 }}>
                            Invitation Links ({links.length})
                        </h2>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={fetchLinks} style={{ ...styles.secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                <RefreshCw size={14} /> Refresh
                            </button>
                            {links.some((l) => l.revoked) && (
                                <button onClick={handleDeleteRevokedAll} style={{ ...styles.secondaryBtn, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <Trash2 size={14} /> Delete Revoked
                                </button>
                            )}
                            {links.some((l) => !l.revoked) && (
                                <button onClick={handleRevokeAll} style={{ ...styles.secondaryBtn, color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <Ban size={14} /> Revoke All
                                </button>
                            )}
                        </div>
                    </div>

                    {linksLoading ? (
                        <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '2rem' }}>Loading links…</p>
                    ) : links.length === 0 ? (
                        <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '2rem' }}>
                            No invitation links yet. Generate one above to get started.
                        </p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        {['Email', 'Label', 'Status', 'Link', 'Expiry', 'Created', 'Last Used', 'Uses', 'Actions'].map((h) => (
                                            <th key={h} style={styles.th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {links.map((link) => {
                                        const status = getLinkStatus(link);
                                        return (
                                            <tr key={link.id} style={styles.tr}>
                                                <td style={styles.td}>{link.email}</td>
                                                <td style={styles.td}>{link.label || '—'}</td>
                                                <td style={styles.td}>
                                                    <span style={{ color: status.color, fontWeight: 600 }}>
                                                        {status.label}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                                        <input
                                                            readOnly
                                                            value={link.url || ''}
                                                            style={{ ...styles.input, marginBottom: 0, padding: '0.3rem 0.5rem', width: '120px', fontSize: '0.75rem' }}
                                                        />
                                                        <button
                                                            onClick={() => handleCopyTable(link.id, link.url)}
                                                            style={{ ...styles.actionBtn, color: '#60a5fa', padding: '0.35rem', background: 'rgba(255,255,255,0.08)', borderRadius: '6px' }}
                                                            title="Copy Link"
                                                        >
                                                            {copiedLinkId === link.id ? <Check size={14} color="#4ade80" /> : <Copy size={14} />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td style={styles.td}>{link.expiresAt ? formatDate(link.expiresAt) : 'Permanent'}</td>
                                                <td style={styles.td}>{formatDate(link.createdAt)}</td>
                                                <td style={styles.td}>{formatDate(link.lastUsed)}</td>
                                                <td style={styles.td}>
                                                    {link.useCount} {link.maxUses !== null ? `/ ${link.maxUses}` : '/ ∞'}
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                        {!link.revoked ? (
                                                            <button
                                                                onClick={() => handleRevoke(link.id)}
                                                                style={{ ...styles.actionBtn, color: '#f59e0b' }}
                                                                title="Revoke link"
                                                            >
                                                                <Ban size={18} />
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    onClick={() => handleReactivate(link.id)}
                                                                    style={{ ...styles.actionBtn, color: '#4ade80' }}
                                                                    title="Reactivate link"
                                                                >
                                                                    <RefreshCw size={18} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleHardDelete(link.id)}
                                                                    style={{ ...styles.actionBtn, color: '#ef4444' }}
                                                                    title="Permanently delete link"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom Modal */}
            {modalConfig.isOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalCard}>
                        <h3 style={styles.modalTitle}>{modalConfig.title}</h3>
                        <p style={styles.modalMessage}>{modalConfig.message}</p>
                        <div style={styles.modalActions}>
                            {modalConfig.type === 'confirm' ? (
                                <>
                                    <button onClick={closeModal} style={styles.modalCancelBtn}>Cancel</button>
                                    <button
                                        onClick={() => { modalConfig.onConfirm(); closeModal(); }}
                                        style={styles.modalConfirmBtn}
                                    >
                                        Confirm
                                    </button>
                                </>
                            ) : (
                                <button onClick={closeModal} style={styles.primaryBtn}>OK</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Inline styles */}
            <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
        </div>
    );
};

// =============================================
// Styles — inline JS objects for self-contained component
// =============================================
const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1a2544 100%)',
        padding: '2rem 1rem',
        fontFamily: "'Inter', system-ui, sans-serif",
        color: '#fff',
    },
    loginCard: {
        maxWidth: 400,
        margin: '8vh auto 0',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16,
        padding: '2.5rem 2rem',
        backdropFilter: 'blur(20px)',
    },
    loginHeader: { textAlign: 'center', marginBottom: '2rem' },
    lockIcon: { display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' },
    loginTitle: { fontSize: '1.5rem', fontWeight: 700, margin: '0.5rem 0 0.25rem' },
    loginSub: { color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', margin: 0 },
    dashboard: { maxWidth: 1200, margin: '0 auto' },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem',
    },
    dashTitle: { fontSize: '1.75rem', fontWeight: 700, margin: 0 },
    dashSub: { color: 'rgba(255,255,255,0.5)', margin: '0.25rem 0 0', fontSize: '0.875rem' },
    card: {
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: '1.75rem',
        marginBottom: '1.5rem',
        backdropFilter: 'blur(10px)',
    },
    cardTitle: { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 1.25rem', color: '#e2e8f0' },
    fieldLabel: {
        display: 'block', fontSize: '0.78rem', fontWeight: 500,
        color: 'rgba(255,255,255,0.6)', marginBottom: '0.4rem', letterSpacing: '0.04em',
        textTransform: 'uppercase',
    },
    input: {
        width: '100%', padding: '0.65rem 0.9rem',
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8, color: '#fff', fontSize: '0.9rem',
        marginBottom: '1rem', outline: 'none', fontFamily: 'inherit',
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '0 1.25rem',
    },
    primaryBtn: {
        width: '100%', padding: '0.75rem 1.5rem',
        background: 'linear-gradient(135deg, #2563eb, #06b6d4)',
        border: 'none', borderRadius: 8, color: '#fff',
        fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit', transition: 'opacity 0.2s',
    },
    secondaryBtn: {
        padding: '0.5rem 1rem',
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 8, color: 'rgba(255,255,255,0.7)',
        fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
    },
    logoutBtn: {
        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.5rem 1.25rem',
        background: 'rgba(239,68,68,0.15)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 8, color: '#f87171',
        fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'inherit',
    },
    errorText: { color: '#f87171', fontSize: '0.85rem', margin: '-0.5rem 0 0.75rem' },
    generatedBox: {
        marginTop: '1.5rem',
        background: 'rgba(34,197,94,0.08)',
        border: '1px solid rgba(34,197,94,0.2)',
        borderRadius: 10, padding: '1.25rem',
    },
    generatedLabel: { color: '#4ade80', fontWeight: 600, margin: '0 0 0.75rem', fontSize: '0.9rem' },
    generatedHint: { color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', margin: '0.5rem 0 0' },
    urlRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
    copyBtn: {
        padding: '0.65rem 1rem', whiteSpace: 'nowrap',
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 8, color: '#fff',
        fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
        marginBottom: '1rem',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
    th: {
        textAlign: 'left', padding: '0.75rem 0.75rem',
        color: 'rgba(255,255,255,0.5)', fontWeight: 500,
        fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    tr: { borderBottom: '1px solid rgba(255,255,255,0.05)' },
    td: { padding: '0.75rem', color: 'rgba(255,255,255,0.8)', verticalAlign: 'middle' },
    actionBtn: {
        background: 'transparent', border: 'none',
        cursor: 'pointer', fontSize: '1rem', padding: '0.25rem',
    },
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, backdropFilter: 'blur(4px)', padding: '1rem',
    },
    modalCard: {
        background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12, padding: '1.5rem', maxWidth: 400, width: '100%',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)',
    },
    modalTitle: { margin: '0 0 0.75rem', fontSize: '1.25rem', fontWeight: 600, color: '#fff' },
    modalMessage: { margin: '0 0 1.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', lineHeight: 1.5 },
    modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' },
    modalCancelBtn: {
        background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
        color: '#e2e8f0', padding: '0.5rem 1rem', borderRadius: 8, cursor: 'pointer',
    },
    modalConfirmBtn: {
        background: '#ef4444', border: 'none', color: '#fff',
        padding: '0.5rem 1.25rem', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
    },
};

export default AdminPage;
